import { Link } from "react-router-dom";
import { Mail, Phone, MapPin, Linkedin, Instagram, Twitter } from "lucide-react";
import Logo from "./Logo";
import { useSiteSettings } from "../context/SiteSettingsContext";
import { useLang } from "../i18n/LanguageContext";
import { COMMON_I18N } from "../i18n/common";

const SOLUTION_LINKS = [
  "/kurumsal-cozumler",
  "/hizmetler/b2b-bayi-sistemi",
  "/hizmetler/ozel-crm-yazilimi",
  "/hizmetler/yapay-zeka-agent",
  "/kurumsal-cozumler",
];

const COMPANY_LINKS = ["/hakkimizda", "/projeler", "/dr-ai", "/blog", "/iletisim"];
const LEGAL_LINKS = ["/yasal#kvkk", "/yasal#gizlilik", "/yasal#cerez"];

export default function Footer() {
  const { settings } = useSiteSettings();
  const { lang } = useLang();
  const t = COMMON_I18N[lang].footer;

  const phone = settings?.contact_phone || "0543 793 41 01";
  const phoneLink = settings?.contact_phone_link || "+905437934101";
  const email = settings?.contact_email || "info@dijitalroket.com";
  const address = settings?.contact_address || (lang === "en" ? "Bursa, Türkiye" : "Bursa, Türkiye");

  const socials = [
    settings?.social_linkedin && { icon: Linkedin, href: settings.social_linkedin, label: "LinkedIn" },
    settings?.social_instagram && { icon: Instagram, href: settings.social_instagram, label: "Instagram" },
    settings?.social_twitter && { icon: Twitter, href: settings.social_twitter, label: "Twitter" },
  ].filter(Boolean);

  return (
    <footer className="bg-[#020617] text-white" data-testid="site-footer">
      <div className="container-x py-20 grid gap-12 lg:grid-cols-4">
        <div>
          <Logo variant="light" />
          <p className="mt-5 text-sm leading-relaxed text-white/60 max-w-xs">{t.about}</p>
          {socials.length > 0 && (
            <div className="mt-6 flex gap-3">
              {socials.map((s, i) => (
                <a
                  key={i}
                  href={s.href}
                  target="_blank"
                  rel="noreferrer"
                  aria-label={`Dijital Roket ${s.label}`}
                  title={`Dijital Roket ${s.label}`}
                  data-testid={`footer-social-${i}`}
                  className="inline-flex h-10 w-10 items-center justify-center rounded-lg border border-white/10 text-white/70 transition hover:bg-white/5 hover:text-white"
                >
                  <s.icon className="h-4 w-4" aria-hidden="true" />
                  <span className="sr-only">{s.label}</span>
                </a>
              ))}
            </div>
          )}
        </div>

        <div>
          <h4 className="text-sm font-bold uppercase tracking-[0.18em] text-[#22D3EE]">{t.colSolutions}</h4>
          <ul className="mt-5 space-y-3 text-sm text-white/70">
            {t.solutions.map((label, i) => (
              <li key={label}><Link to={SOLUTION_LINKS[i]} className="hover:text-white">{label}</Link></li>
            ))}
          </ul>
        </div>

        <div>
          <h4 className="text-sm font-bold uppercase tracking-[0.18em] text-[#22D3EE]">{t.colCompany}</h4>
          <ul className="mt-5 space-y-3 text-sm text-white/70">
            {t.company.map((label, i) => (
              <li key={label}><Link to={COMPANY_LINKS[i]} className="hover:text-white">{label}</Link></li>
            ))}
          </ul>
        </div>

        <div>
          <h4 className="text-sm font-bold uppercase tracking-[0.18em] text-[#22D3EE]">{t.colContact}</h4>
          <ul className="mt-5 space-y-3 text-sm text-white/70">
            <li className="flex items-start gap-2">
              <Phone className="h-4 w-4 mt-0.5 text-[#22D3EE]" />
              <a href={`tel:${phoneLink}`} className="hover:text-white">{phone}</a>
            </li>
            <li className="flex items-start gap-2">
              <Mail className="h-4 w-4 mt-0.5 text-[#22D3EE]" />
              <a href={`mailto:${email}`} className="hover:text-white break-all">{email}</a>
            </li>
            <li className="flex items-start gap-2"><MapPin className="h-4 w-4 mt-0.5 text-[#22D3EE]" /> {address}</li>
          </ul>

          <h4 className="mt-8 text-sm font-bold uppercase tracking-[0.18em] text-[#22D3EE]">{t.colLegal}</h4>
          <ul className="mt-5 space-y-3 text-sm text-white/70">
            {t.legal.map((label, i) => (
              <li key={label}><Link to={LEGAL_LINKS[i]} className="hover:text-white">{label}</Link></li>
            ))}
          </ul>
        </div>
      </div>

      <div className="border-t border-white/5">
        <div className="container-x py-6 flex flex-col sm:flex-row items-center justify-between gap-3 text-xs text-white/40">
          <span>{t.rights}</span>
          <span>{t.tagline}</span>
        </div>
      </div>
    </footer>
  );
}
